import React, { useEffect, useRef, useState } from 'react';
import { fetchPlacaResumo } from '../../../../api/contac/contacAnalyticsApi';
import {
  downloadRelatorioCsvDrePlacas,
  downloadRelatorioXlsxDrePlacas,
} from '../../../../api/contac/contacReportsPlacasApi';
import FullScreenLoader from '../../../../components/loading/FullScreenLoader';
import ArrowDown from '../../../../img/arrow-down.svg';
import ArrowUp from '../../../../img/arrow-up.svg';
import ContacDocumentOrange from '../../../../img/contac_document_orange.svg';
import { toast } from 'react-toastify';
import './ModalPesquisaPorPlaca.css';
import '../placa-dre/ModalPlacaDre.css';
import '../filtro/ModalFiltro.css';
import '../../../../shared/ui/modal-close-button.css'; // X padrão do sistema
import { removerAcentos } from '../../../../shared/texto/busca-sem-acento';

interface ModalPesquisaPorPlacaProps {
  isOpen: boolean;
  onCancel: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

interface Detalhe {
  mes: string;
  valor: number;
}
interface ClassificacaoResp {
  classificacao: string;
  total: number;
  detalhes: Detalhe[];
}

interface Detalhamento {
  receitaMensalidade: number;
  receitaNasajonComPlaca: number;
  receitaRateio: number;

  despesaNasajonComPlaca: number;
  despesaEasy: number;
  despesaRateio: number;
  despesaEasyExcedente: number;
  despesaRecorrente: number;
  despesaRecorrenteExcedente: number;

  totaisEntrada: number;
  totaisSaida: number;
  resultado: number;
}

interface ResultadoPlaca {
  placa: string;
  modelo: string;
  ano_modelo: number;
  categoria: string;
  associado: string;
  cpf_associado: string;
  tempo_participacao_meses: number;
  regional: string;
  consultor: string;
  cidade: string;
  situacao: string;
  resultado: number;
  detalhamento: Detalhamento;

  entradasClassificacoes: ClassificacaoResp[];
  saidasClassificacoes: ClassificacaoResp[];
  entradasClassificacoesRateio?: ClassificacaoResp[];
  saidasClassificacoesRateio?: ClassificacaoResp[];
}

const classBySign3 = (n?: number) => (typeof n === 'number' && n < 0 ? 'value-negative3' : 'value-positive3');

const formatCurrency = (valor: number | null | undefined) =>
  typeof valor === 'number' ? valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '0,00';

const formatPercent = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n)
    ? `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    : '—';

const pctOf = (signedValue: number, baseAbs: number) =>
  baseAbs === 0 ? null : (signedValue / baseAbs) * 100;

const renderValorPct = (
  displayValue: React.ReactNode,
  signedForPercent: number,
  baseAbs: number,
  percentClassName?: string
) => (
  <div className="dre-value-pct">
    {displayValue}
    <span className={`dre-percent ${percentClassName ?? ''}`}>
      {formatPercent(pctOf(signedForPercent, baseAbs))}
    </span>
  </div>
);

const toNum = (v: any) => (typeof v === 'number' ? v : Number(v ?? 0)) || 0;
const norm = (v: any) => removerAcentos(String(v ?? '').trim().toUpperCase());
const upper = (v: any) => String(v ?? '').trim().toUpperCase();

const mapClassifs = (arr: any[] = []): ClassificacaoResp[] =>
  arr.map((c: any) => ({
    classificacao: c?.classificacao ?? 'Sem Classificação',
    total: toNum(c?.total),
    detalhes: (Array.isArray(c?.porMes) ? c.porMes : c?.detalhes || []).map((m: any) => ({
      mes: m?.competencia ?? m?.mes ?? '',
      valor: toNum(m?.valor),
    })),
  }));

const fmtDate = (y: number, m: number, d: number) =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const ModalPesquisaPorPlaca: React.FC<ModalPesquisaPorPlacaProps> = ({ isOpen, onCancel, sidebarRef }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const resultadoRef = useRef<HTMLDivElement>(null);

  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() - 2);
  const maxYear = maxDate.getFullYear();
  const maxMonth = maxDate.getMonth() + 1; // 1-indexed

  const [placa, setPlaca] = useState('');
  const [startYear, setStartYear] = useState(maxYear);
  const [startMonth, setStartMonth] = useState(maxMonth);
  const [endYear, setEndYear] = useState(maxYear);
  const [endMonth, setEndMonth] = useState<number>(maxMonth);

  const yearOptions: number[] = [];
  for (let y = 2025; y <= maxYear; y++) {
    yearOptions.push(y);
  }

  const ALLOWED_MONTHS = [
    { num: 1, name: 'Janeiro' },
    { num: 2, name: 'Fevereiro' },
    { num: 3, name: 'Março' },
    { num: 4, name: 'Abril' },
    { num: 5, name: 'Maio' },
    { num: 6, name: 'Junho' },
    { num: 7, name: 'Julho' },
    { num: 8, name: 'Agosto' },
    { num: 9, name: 'Setembro' },
    { num: 10, name: 'Outubro' },
    { num: 11, name: 'Novembro' },
    { num: 12, name: 'Dezembro' },
  ];

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [resultado, setResultado] = useState<ResultadoPlaca | null | 'no-search'>('no-search');
  const hasResultado = !!resultado && resultado !== 'no-search';

  const [expanded, setExpanded] = useState<string[]>([]);
  const toggleExpand = (key: string) =>
    setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const handleDownloadCsv = async () => {
    try {
      setDownloading(true);

      const placaRaw = String((typeof resultado === 'object' && resultado ? resultado.placa : placa) ?? '').trim().toUpperCase();
      const placaKey = placaRaw.replace(/[^A-Z0-9]/g, '');

      const dataInicio = fmtDate(startYear, startMonth, 1);
      const lastDay = new Date(endYear, endMonth, 0).getDate();
      const dataFim = fmtDate(endYear, endMonth, lastDay);

      await downloadRelatorioCsvDrePlacas({
        dataInicio,
        dataFim,
        placas: placaKey ? [placaKey] : ['SEM PLACA DEFINIDA'],
      });

      toast.success('Relatório CSV gerado com sucesso!');
    } catch {
      toast.error('Erro ao gerar CSV');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadXlsx = async () => {
    try {
      setDownloading(true);

      const placaRaw = String((typeof resultado === 'object' && resultado ? resultado.placa : placa) ?? '').trim().toUpperCase();
      const placaKey = placaRaw.replace(/[^A-Z0-9]/g, '');

      const dataInicio = fmtDate(startYear, startMonth, 1);
      const lastDay = new Date(endYear, endMonth, 0).getDate();
      const dataFim = fmtDate(endYear, endMonth, lastDay);

      await downloadRelatorioXlsxDrePlacas({
        dataInicio,
        dataFim,
        placas: placaKey ? [placaKey] : ['SEM PLACA DEFINIDA'],
      });

      toast.success('Relatório XLSX gerado com sucesso!');
    } catch {
      toast.error('Erro ao gerar XLSX');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setResultado('no-search');
      setExpanded([]);
      return;
    }

    const isOutside = (ref: React.RefObject<HTMLElement>, target: Node) => !ref.current || !ref.current.contains(target);

    const handlePointerDown = (event: MouseEvent) => {
      const t = event.target as Node;

      const outsidePesquisa = isOutside(modalRef, t);
      const outsideResultado = isOutside(resultadoRef, t);
      const outsideSidebar = !sidebarRef?.current || !sidebarRef.current.contains(t);

      if (outsidePesquisa && outsideResultado && outsideSidebar) onCancel();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onCancel, sidebarRef]);

  if (!isOpen) return null;

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();

    const placaKey = placa.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!placaKey) return;

    const start = new Date(startYear, startMonth - 1, 1);
    const end = new Date(endYear, endMonth - 1, 1);

    if (start > end) {
      toast.error('A data final não pode ser menor que a data inicial.');
      return;
    }

    const dataInicio = fmtDate(startYear, startMonth, 1);
    const lastDay = new Date(endYear, endMonth, 0).getDate();
    const dataFim = fmtDate(endYear, endMonth, lastDay);

    try {
      setLoading(true);

      const res0: any = await fetchPlacaResumo({ placa: placaKey, dataInicio, dataFim });

      const res =
        res0?.item ?? (Array.isArray(res0?.items) ? res0.items[0] : null) ?? res0 ?? null;

      if (!res?.detalhamento) {
        setResultado(null);
        setExpanded([]);
        return;
      }

      const d: any = res.detalhamento ?? {};
      const mesesMap = Object.fromEntries(
        Object.entries(d).filter(([k, v]) => /^\d{2}\/\d{4}$/.test(k) && Array.isArray(v)),
      ) as Record<string, any[]>;
      const linhas = Object.entries(mesesMap).flatMap(([mes, arr]) => (arr ?? []).map((x: any) => ({ ...x, __mes: mes })));

      const getGrupo = (x: any) => {
        const grupo = norm(x?.grupo);
        const tipo = norm(x?.tipo);
        const classificacao = norm(x?.classificacao);
        const fonte = norm(x?.fonte);

        if (grupo) return grupo;

        if (tipo === 'ENTRADA') {
          if (classificacao.includes('RATEIO')) return 'OUTRAS RECEITAS';
          return 'RECEITA OPERACIONAL';
        }

        if (tipo === 'SAIDA') {
          if (
            classificacao.includes('EXCEDENTE') ||
            classificacao.includes('RATEIO') ||
            fonte.includes('EXCEDENTE') ||
            fonte.includes('RATEIO')
          ) return 'MARGEM DE CONTRIBUICAO';
          return 'DESPESAS OPERACIONAIS';
        }

        return '';
      };

      const sumWhere = (pred: (x: any) => boolean) =>
        linhas.reduce((acc, x) => acc + (pred(x) ? toNum(x?.valor_total) : 0), 0);

      const sumGrupo = (grupo: string, tipo: 'ENTRADA' | 'SAIDA') =>
        sumWhere((x) => upper(x?.bloco) === 'DETALHE' && x?.tipo === tipo && getGrupo(x) === norm(grupo));

      const isGrupoMargem = (x: any) => getGrupo(x).includes('MARGEM');

      const sumMargem = () =>
        sumWhere((x) => upper(x?.bloco) === 'DETALHE' && x?.tipo === 'SAIDA' && isGrupoMargem(x));

      const buildClassificacoesRaw = (grupo: string | ((x: any) => boolean), tipo: 'ENTRADA' | 'SAIDA') => {
        const acc: Record<string, { classificacao: string; total: number; porMes: Record<string, number> }> = {};
        for (const x of linhas) {
          if (upper(x?.bloco) !== 'DETALHE') continue;
          if (x?.tipo !== tipo) continue;
          if (typeof grupo === 'function') {
            if (!grupo(x)) continue;
          } else {
            if (getGrupo(x) !== norm(grupo)) continue;
          }
          const key = String(x?.classificacao ?? '').trim() || 'Sem Classificação';
          const v = toNum(x?.valor_total);
          if (!acc[key]) acc[key] = { classificacao: key, total: 0, porMes: {} };
          acc[key].total += v;
          acc[key].porMes[x.__mes] = (acc[key].porMes[x.__mes] ?? 0) + v;
        }
        return Object.values(acc)
          .map((c) => ({
            classificacao: c.classificacao,
            total: c.total,
            porMes: Object.entries(c.porMes).map(([competencia, valor]) => ({ competencia, valor })),
          }))
          .sort((a, b) => b.total - a.total);
      };

      const receitaMensalidade = sumWhere(
        (x) => upper(x?.bloco) === 'DETALHE' && x?.tipo === 'ENTRADA' && getGrupo(x) === 'RECEITA OPERACIONAL' && upper(x?.fonte).includes('MV_SGA'),
      );

      const receitaNasajonComPlaca = sumWhere(
        (x) => upper(x?.bloco) === 'DETALHE' && x?.tipo === 'ENTRADA' && getGrupo(x) === 'RECEITA OPERACIONAL' && upper(x?.fonte).includes('MV_NASAJON'),
      );

      const receitaRateio = sumGrupo('OUTRAS RECEITAS', 'ENTRADA');

      const despesaNasajonComPlaca = sumWhere(
        (x) => upper(x?.bloco) === 'DETALHE' && x?.tipo === 'SAIDA' && getGrupo(x) === 'DESPESAS OPERACIONAIS' && upper(x?.fonte).includes('MV_NASAJON'),
      );

      const despesaEasyExcedente = sumWhere(
        (x) => upper(x?.bloco) === 'DETALHE' && x?.tipo === 'SAIDA' && isGrupoMargem(x) && upper(x?.fonte).includes('EASY_EXCEDENTE'),
      );

      const despesaEasy = sumWhere(
        (x) => upper(x?.bloco) === 'DETALHE' && x?.tipo === 'SAIDA' && getGrupo(x) === 'DESPESAS OPERACIONAIS' && upper(x?.fonte).includes('EASYASSIST'),
      );

      const despesaRecorrenteExcedente = sumWhere(
        (x) => upper(x?.bloco) === 'DETALHE' && x?.tipo === 'SAIDA' && isGrupoMargem(x) && upper(x?.fonte).includes('RECORRENTE_EXCEDENTE'),
      );

      const despesaRecorrente = sumWhere(
        (x) => upper(x?.bloco) === 'DETALHE' && x?.tipo === 'SAIDA' && getGrupo(x) === 'DESPESAS OPERACIONAIS' && upper(x?.fonte).includes('MV_RECORRENTE') && !upper(x?.fonte).includes('EXCEDENTE'),
      );

      const despesaRateio = sumMargem();

      const receitaOperacionalGrupo = sumGrupo('RECEITA OPERACIONAL', 'ENTRADA');
      const outrasReceitasGrupo = receitaRateio;
      const despesasOperacionaisGrupo = sumGrupo('DESPESAS OPERACIONAIS', 'SAIDA');
      const margemContribuicaoGrupo = despesaRateio;

      const totaisEntrada = toNum(d?.totaisEntrada) || (receitaOperacionalGrupo + outrasReceitasGrupo);
      const totaisSaida = toNum(d?.totaisSaida) || (despesasOperacionaisGrupo + margemContribuicaoGrupo);
      const resultadoBack = toNum(d?.resultado) || (totaisEntrada - totaisSaida);

      const entradasOp = mapClassifs(buildClassificacoesRaw('RECEITA OPERACIONAL', 'ENTRADA'));
      const entradasRateio = mapClassifs(buildClassificacoesRaw('OUTRAS RECEITAS', 'ENTRADA'));
      const saidasOp = mapClassifs(buildClassificacoesRaw('DESPESAS OPERACIONAIS', 'SAIDA'));
      const saidasRateio = mapClassifs(buildClassificacoesRaw((x: any) => isGrupoMargem(x), 'SAIDA'));

      const adapted: ResultadoPlaca = {
        placa: String(res?.placa ?? placaKey),
        modelo: String(res?.modelo ?? ''),
        ano_modelo: Number(res?.ano_modelo) || 0,
        categoria: String(res?.categoria ?? ''),
        associado: String(res?.associado ?? ''),
        cpf_associado: String(res?.cpf_associado ?? ''),
        tempo_participacao_meses: Number(res?.tempo_participacao_meses) || 0,
        regional: String(res?.regional ?? ''),
        consultor: String(res?.consultor ?? ''),
        cidade: String(res?.cidade ?? ''),
        situacao: String(res?.situacao ?? (res?.is_ativa === true ? 'ATIVA' : (res?.is_ativa === false ? 'INATIVA' : ''))),
        resultado: resultadoBack,
        detalhamento: {
          receitaMensalidade,
          receitaNasajonComPlaca,
          receitaRateio,
          despesaNasajonComPlaca,
          despesaEasy,
          despesaRateio,
          despesaEasyExcedente,
          despesaRecorrente,
          despesaRecorrenteExcedente,
          totaisEntrada,
          totaisSaida,
          resultado: resultadoBack,
        },
        entradasClassificacoes: entradasOp,
        saidasClassificacoes: saidasOp,
        entradasClassificacoesRateio: entradasRateio,
        saidasClassificacoesRateio: saidasRateio,
      };

      setResultado(adapted);
      setExpanded([]);
    } catch (err) {
      setResultado(null);
      setExpanded([]);
      toast.error('Erro ao buscar resultado para a placa.');
    } finally {
      setLoading(false);
    }
  };

  const r = typeof resultado === 'object' && resultado ? resultado : null;

  const receitaBruta = Number(r?.detalhamento?.totaisEntrada) || 0;
  const resultadoFinal = Number(r?.detalhamento?.resultado) || 0;

  const receitaOperacional =
    (Number(r?.detalhamento?.receitaMensalidade) || 0) + (Number(r?.detalhamento?.receitaNasajonComPlaca) || 0);
  const outrasReceitas = Number(r?.detalhamento?.receitaRateio) || 0;

  const despesasOperacionais =
    (Number(r?.detalhamento?.despesaNasajonComPlaca) || 0) +
    (Number(r?.detalhamento?.despesaEasy) || 0) +
    (Number(r?.detalhamento?.despesaRecorrente) || 0);

  const margemContribuicao = Math.abs(Number(r?.detalhamento?.despesaRateio) || 0);
  const resultadoOperacional = receitaBruta - despesasOperacionais;
  const baseRbAbs = Math.abs(receitaBruta ?? 0);

  return (
    <>
      {(loading || downloading) && <FullScreenLoader />}

      <div className="status-modal-overlay">
      {!hasResultado && (
        <div className="status-modal-content" ref={modalRef} style={{ width: 'min(460px, 92vw)' }}>
          <div className="filtro-modal-card">
            
            {/* Header */}
            <div className="filtro-modal-header">
              <div>
                <h2 className="filtro-modal-title">Pesquisa por Placa</h2>
                <p className="filtro-modal-subtitle">Busque resultados financeiros de um veículo</p>
              </div>
              <button type="button" className="close-button" onClick={onCancel}>&times;</button>
            </div>

            {/* Form */}
            <form onSubmit={handleConfirm}>
              <div className="filtro-modal-body">
                
                {/* Placa */}
                <div className="filtro-field">
                  <label style={{ fontFamily: "'AmpleSoft-Bold', sans-serif", fontSize: '13px', color: 'var(--tema-texto, #1A1C1B)' }}>Placa do Veículo</label>
                  <input
                    type="text"
                    value={placa}
                    onChange={(e) => setPlaca(e.target.value.toUpperCase())}
                    className="premium-text-input"
                    placeholder="Ex: SRW2J50"
                    required
                  />
                </div>

                {/* Período Inicial */}
                <div className="filtro-grid-2">
                  <div className="filtro-field">
                    <label>Mês Inicial</label>
                    <div className="select-wrapper">
                      <select
                        value={startMonth}
                        onChange={(e) => setStartMonth(Number(e.target.value))}
                      >
                        {ALLOWED_MONTHS.filter((m) => startYear < maxYear || m.num <= maxMonth).map((m) => (
                          <option key={m.num} value={m.num}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="filtro-field">
                    <label>Ano Inicial</label>
                    <div className="select-wrapper">
                      <select
                        value={startYear}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setStartYear(v);
                          if (v === maxYear && startMonth > maxMonth) {
                            setStartMonth(maxMonth);
                          }
                        }}
                      >
                        {yearOptions.map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Período Final */}
                <div className="filtro-grid-2">
                  <div className="filtro-field">
                    <label>Mês Final</label>
                    <div className="select-wrapper">
                      <select
                        value={endMonth}
                        onChange={(e) => setEndMonth(Number(e.target.value))}
                      >
                        {ALLOWED_MONTHS.filter((m) => endYear < maxYear || m.num <= maxMonth).map((m) => (
                          <option key={m.num} value={m.num}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="filtro-field">
                    <label>Ano Final</label>
                    <div className="select-wrapper">
                      <select
                        value={endYear}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setEndYear(v);
                          if (v === maxYear && endMonth > maxMonth) {
                            setEndMonth(maxMonth);
                          }
                        }}
                      >
                        {yearOptions.map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {resultado === null && (
                  <div className="resultado-vazio-new">
                    Não existem dados para essa placa nesse período.
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="filtro-modal-footer">
                <button type="button" className="filtro-btn-secondary" onClick={onCancel}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="filtro-btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Pesquisando...' : 'Confirmar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {hasResultado && r && (
        <div className="status-modal-dre-content" ref={resultadoRef} style={{ margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div className="modal-header">{r.placa}</div>

            <div
              className="dre-save-btn MouseHover"
              onClick={handleDownloadCsv}
              aria-disabled={downloading}
              style={{ opacity: downloading ? 0.6 : 1, pointerEvents: downloading ? 'none' : 'auto' }}
              title="Gerar CSV do DRE da placa"
            >
              <div className="Frame1000003987">
                <img
                  src={ContacDocumentOrange}
                  alt="Relatórios"
                  width="19.80"
                  height="19.80"
                  style={{ cursor: 'pointer' }}
                />
                <div className="ComponentName">{downloading ? 'Gerando...' : 'Gerar CSV'}</div>
              </div>
            </div>

            <div
              className="dre-save-btn MouseHover"
              onClick={handleDownloadXlsx}
              aria-disabled={downloading}
              style={{ opacity: downloading ? 0.6 : 1, pointerEvents: downloading ? 'none' : 'auto' }}
              title="Gerar XLSX do DRE da placa"
            >
              <div className="Frame1000003987">
                <img
                  src={ContacDocumentOrange}
                  alt="Relatórios"
                  width="19.80"
                  height="19.80"
                  style={{ cursor: 'pointer' }}
                />
                <div className="ComponentName">{downloading ? 'Gerando...' : 'Gerar XLSX'}</div>
              </div>
            </div>
          </div>

          <div className="modal-body">
            {/* Card 1 - Dados do Veículo */}
            <div className="card card-left">
              <div className="modal-header">Dados do Veículo</div>
              {[
                ['Associado', r.associado],
                ['CPF', r.cpf_associado],
                ['Placa', r.placa],
                ['Veículo', r.modelo],
                ['Ano Mod', String(r.ano_modelo || '')],
                ['Categoria', r.categoria],
                ['Tempo de Associação', `${r.tempo_participacao_meses} meses`],
                ['Regional', r.regional],
                ['Consultor', r.consultor],
                ['Cidade', r.cidade],
                ['Situação', r.situacao],
              ].map(([label, valor], idx) => (
                <div className="info-row" key={idx}>
                  <span className="info-label">{label}</span>
                  <span className="info-value">{String(valor ?? '')}</span>
                </div>
              ))}
            </div>

            {/* Card 2 - DRE */}
            <div className="card">
              <div className="modal-header">Demonstrativo de Resultados (DRE)</div>
              <div className="row-between dre-columns-header">
                <div />
                <div className="dre-value-pct">
                  <span className="dre-col-header" style={{ minWidth: 120, textAlign: 'right' }}>Valor</span>
                  <span className="dre-col-header" style={{ minWidth: 70, textAlign: 'right' }}>% Rec. Bruta</span>
                </div>
              </div>

              <div className="row-between">
                <div className="dre-title">
                  Receita Bruta <span className="dre-note">(Todas as Receitas)</span>
                </div>
                {renderValorPct(
                  <span className="entry-text value-positive3">R$ {formatCurrency(receitaBruta)}</span>,
                  +Math.abs(receitaBruta),
                  baseRbAbs,
                  'value-positive5'
                )}
              </div>

              <div
                className="row-between clickable"
                onClick={() => toggleExpand('op')}
                aria-expanded={expanded.includes('op')}
                title="Ver detalhes da Receita Operacional"
              >
                <div className="dre-title">
                  Receita Operacional <span className="dre-note">(Receitas ligadas às placas)</span>
                  <img
                    style={{ marginLeft: 5 }}
                    src={expanded.includes('op') ? ArrowUp : ArrowDown}
                    alt="Toggle"
                    className="imgIcon arrow-toggle"
                  />
                </div>
                {renderValorPct(
                  <span className="entry-text value-positive3">R$ {formatCurrency(receitaOperacional)}</span>,
                  +Math.abs(receitaOperacional),
                  baseRbAbs,
                  'value-positive5'
                )}
              </div>

              {expanded.includes('op') && (
                <div className="subclass-container">
                  {(r.entradasClassificacoes || []).map((ent, idx) => (
                    <React.Fragment key={`ent-op-${idx}`}>
                      <div className="subclass-row">
                        <div className="subclass-label">
                          <span>{ent.classificacao}</span>
                          <img
                            src={expanded.includes(`ent-op-${idx}`) ? ArrowUp : ArrowDown}
                            alt="Toggle"
                            className="imgIcon arrow-toggle clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(`ent-op-${idx}`);
                            }}
                          />
                        </div>
                        <div className="subclass-value-pct">
                          <span className="subclass-value value-positive4">R$ {formatCurrency(ent.total)}</span>
                          <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                        </div>
                      </div>

                      {expanded.includes(`ent-op-${idx}`) &&
                        (ent.detalhes || []).map((det, dIdx) => (
                          <div className="detail-row" key={`ent-op-det-${idx}-${dIdx}`}>
                            <span className="detail-label">{det.mes}</span>
                            <div className="subclass-value-pct">
                              <span className="detail-value value-positive4">R$ {formatCurrency(det.valor)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>
                        ))}
                    </React.Fragment>
                  ))}
                </div>
              )}

              <div
                className="row-between clickable"
                onClick={() => toggleExpand('oe')}
                aria-expanded={expanded.includes('oe')}
                title="Ver detalhes de Outras Receitas"
              >
                <div className="dre-title">
                  Outras Receitas <span className="dre-note">(Receitas não ligadas às placas)</span>
                  <img
                    style={{ marginLeft: 5 }}
                    src={expanded.includes('oe') ? ArrowUp : ArrowDown}
                    alt="Toggle"
                    className="imgIcon arrow-toggle"
                  />
                </div>
                {renderValorPct(
                  <span className="entry-text value-positive3">R$ {formatCurrency(outrasReceitas)}</span>,
                  +Math.abs(outrasReceitas),
                  baseRbAbs,
                  'value-positive5'
                )}
              </div>

              {expanded.includes('oe') && (
                <div className="subclass-container">
                  {(r.entradasClassificacoesRateio || []).map((ent, idx) => (
                    <React.Fragment key={`ent-rateio-${idx}`}>
                      <div className="subclass-row">
                        <div className="subclass-label">
                          <span>{ent.classificacao}</span>
                          <img
                            src={expanded.includes(`ent-rateio-${idx}`) ? ArrowUp : ArrowDown}
                            alt="Toggle"
                            className="imgIcon arrow-toggle clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(`ent-rateio-${idx}`);
                            }}
                          />
                        </div>
                        <div className="subclass-value-pct">
                          <span className="subclass-value value-positive4">R$ {formatCurrency(ent.total)}</span>
                          <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                        </div>
                      </div>

                      {expanded.includes(`ent-rateio-${idx}`) &&
                        (ent.detalhes || []).map((det, dIdx) => (
                          <div className="detail-row" key={`ent-rateio-det-${idx}-${dIdx}`}>
                            <span className="detail-label">{det.mes}</span>
                            <div className="subclass-value-pct">
                              <span className="detail-value value-positive4">R$ {formatCurrency(det.valor)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>
                        ))}
                    </React.Fragment>
                  ))}
                </div>
              )}

              <div
                className="row-between clickable"
                onClick={() => toggleExpand('co')}
                aria-expanded={expanded.includes('co')}
                title="Ver detalhes de Despesas Operacionais"
              >
                <div className="dre-title">
                  Despesas Operacionais <span className="dre-note">(Despesas ligadas às placas)</span>
                  <img
                    style={{ marginLeft: 5 }}
                    src={expanded.includes('co') ? ArrowUp : ArrowDown}
                    alt="Toggle"
                    className="imgIcon arrow-toggle"
                  />
                </div>
                {renderValorPct(
                  <span className="entry-text value-negative3">R$ -{formatCurrency(despesasOperacionais)}</span>,
                  -Math.abs(despesasOperacionais),
                  baseRbAbs,
                  'value-negative5'
                )}
              </div>

              {expanded.includes('co') && (
                <div className="subclass-container">
                  {(r.saidasClassificacoes || [])
                    .filter((x) => (Number(x.total) || 0) !== 0)
                    .map((sai, idx) => (
                      <React.Fragment key={`sai-dir-${idx}`}>
                        <div className="subclass-row">
                          <div className="subclass-label">
                            <span>{sai.classificacao}</span>
                            <img
                              src={expanded.includes(`sai-dir-${idx}`) ? ArrowUp : ArrowDown}
                              alt="Toggle"
                              className="imgIcon arrow-toggle clickable"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(`sai-dir-${idx}`);
                              }}
                            />
                          </div>
                          <div className="subclass-value-pct">
                            <span className="subclass-value value-negative4">R$ {formatCurrency(sai.total)}</span>
                            <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                          </div>
                        </div>

                        {expanded.includes(`sai-dir-${idx}`) &&
                          (sai.detalhes || []).map((det, dIdx) => (
                            <div className="detail-row" key={`sai-dir-det-${idx}-${dIdx}`}>
                              <span className="detail-label">{det.mes}</span>
                              <div className="subclass-value-pct">
                                <span className="detail-value value-negative4">R$ {formatCurrency(det.valor)}</span>
                                <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                              </div>
                            </div>
                          ))}
                      </React.Fragment>
                    ))}
                </div>
              )}

              <div className="row-between">
                <div className="dre-title">
                  Resultado Operacional <span className="dre-note">(Receita Bruta - Despesa Operacional)</span>
                </div>
                {renderValorPct(
                  <span className={`entry-text ${classBySign3(resultadoOperacional)}`}>
                    R$ {formatCurrency(resultadoOperacional)}
                  </span>,
                  Number(resultadoOperacional) || 0,
                  baseRbAbs,
                  (Number(resultadoOperacional) || 0) < 0 ? 'value-negative5' : 'value-positive5'
                )}
              </div>

              <div
                className="row-between clickable"
                onClick={() => toggleExpand('mc')}
                aria-expanded={expanded.includes('mc')}
                title="Ver detalhes de Margem de Contribuição"
              >
                <div className="dre-title">
                  Margem de Contribuição <span className="dre-note">(Rateio de despesas não ligadas às placas)</span>
                  <img
                    style={{ marginLeft: 5 }}
                    src={expanded.includes('mc') ? ArrowUp : ArrowDown}
                    alt="Toggle"
                    className="imgIcon arrow-toggle"
                  />
                </div>
                {renderValorPct(
                  <span className="entry-text value-negative3">R$ -{formatCurrency(margemContribuicao)}</span>,
                  -Math.abs(margemContribuicao),
                  baseRbAbs,
                  'value-negative5'
                )}
              </div>

              {expanded.includes('mc') && (
                <div className="subclass-container">
                  {(r.saidasClassificacoesRateio || []).map((sai, idx) => (
                    <React.Fragment key={`sai-rateio-${idx}`}>
                      <div className="subclass-row">
                        <div className="subclass-label">
                          <span>{sai.classificacao}</span>
                          <img
                            src={expanded.includes(`sai-rateio-${idx}`) ? ArrowUp : ArrowDown}
                            alt="Toggle"
                            className="imgIcon arrow-toggle clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(`sai-rateio-${idx}`);
                            }}
                          />
                        </div>
                        <div className="subclass-value-pct">
                          <span className="subclass-value value-negative4">R$ {formatCurrency(sai.total)}</span>
                          <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                        </div>
                      </div>

                      {expanded.includes(`sai-rateio-${idx}`) &&
                        (sai.detalhes || []).map((det, dIdx) => (
                          <div className="detail-row" key={`sai-rateio-det-${idx}-${dIdx}`}>
                            <span className="detail-label">{det.mes}</span>
                            <div className="subclass-value-pct">
                              <span className="detail-value value-negative4">R$ {formatCurrency(det.valor)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>
                        ))}
                    </React.Fragment>
                  ))}
                </div>
              )}

              <div className="row-between">
                <div className="dre-title">
                  Resultado <span className="dre-note">(Resultado Operacional - Margem de Contribuição)</span>
                </div>
                {renderValorPct(
                  <span className={`entry-text ${classBySign3(resultadoFinal)}`}>R$ {formatCurrency(resultadoFinal)}</span>,
                  Number(resultadoFinal) || 0,
                  baseRbAbs,
                  (Number(resultadoFinal) || 0) < 0 ? 'value-negative5' : 'value-positive5'
                )}
              </div>

              <div className="modal-footer">
                <button className="modal-confirm-btn" style={{ marginRight: 10 }} onClick={() => setResultado('no-search')}>
                  Nova pesquisa
                </button>
                <button className="modal-close-btn" onClick={onCancel}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default ModalPesquisaPorPlaca;
